import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  MdCheckCircle,
  MdClear,
  MdFilterList,
  MdPeopleOutline,
  MdPendingActions,
  MdRefresh,
} from "react-icons/md";
import {
  Button,
  Chip,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";

import { useAppContext } from "../contexts/AppContextProvider";
import {
  convertToIst,
  isoTimestampToReadable,
} from "../utils/timeCalculator.js";
import { camelCaseToCapitalCase } from "../utils/inputValidations.js";
import {
  isInvalidDateRange,
  parseFilterDate,
} from "../utils/partnerFilterUtils.js";
import { CustomTable } from "./index.js";

const DEFAULT_FILTERS = {
  registrationDateFrom: "",
  registrationDateTo: "",
  businessType: "",
  registrationStatus: "",
};

const DEFAULT_REGISTRATION_STATUS_OPTION = [
  { label: "All", value: "" },
  { label: "Confirmed", value: "Confirmed" },
  { label: "Pending", value: "Pending" },
];

const Registrations = () => {
  const { getRegistrationsByPartner, registrationsByPartner } = useAppContext();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const showFilters = Boolean(filterAnchorEl);
  const theme = localStorage.getItem("theme") || "dark";

  const registeredUsersColumns = [
    {
      field: "regDate",
      headerName: "Reg. Date",
      valueGetter: (value, row) =>
        isoTimestampToReadable(convertToIst(row?.regDate)),
      width: 200,
    },
    { field: "firstName", headerName: "First Name", width: 250 },
    { field: "lastName", headerName: "Last Name", width: 250 },
    { field: "businessType", headerName: "Business Type", width: 200 },
    { field: "businessName", headerName: "Business Name", width: 350 },
    {
      field: "phoneNumber",
      headerName: "Phone No.",
      width: 150,
      sortable: false,
    },
    { field: "email", headerName: "Email", width: 250 },
    {
      field: "registrationStatus",
      headerName: "Registration Status",
      width: 150,
      renderCell: (params) => {
        const isConfirmed = params.row.registrationStatus === "Confirmed";

        return (
          <Chip
            icon={isConfirmed ? <MdCheckCircle /> : <MdPendingActions />}
            label={params.row.registrationStatus}
            size="small"
            sx={{
              fontWeight: 700,
              color: isConfirmed ? "#047857" : "#b45309",
              backgroundColor: isConfirmed ? "#d1fae5" : "#fef3c7",
              "& .MuiChip-icon": {
                color: "inherit",
                fontSize: 17,
              },
            }}
          />
        );
      },
    },
  ];

  const handleApplyFilters = () => {
    if (
      isInvalidDateRange(
        filters.registrationDateFrom,
        filters.registrationDateTo,
      )
    ) {
      return toast.warn(
        "The start date must be before or equal to the end date.",
      );
    }

    setAppliedFilters(filters);
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);

    setAppliedFilters(DEFAULT_FILTERS);
    setFilterAnchorEl(null);
  };

  const filteredData = useMemo(() => {
    if (!registrationsByPartner) return [];

    return registrationsByPartner?.filter((row) => {
      // ── registrationDate range ──
      if (
        appliedFilters.registrationDateFrom ||
        appliedFilters.registrationDateTo
      ) {
        const regDate = parseFilterDate(row.regDate);
        if (!regDate) return false;
        if (
          appliedFilters.registrationDateFrom &&
          regDate < parseFilterDate(appliedFilters.registrationDateFrom)
        )
          return false;
        if (appliedFilters.registrationDateTo) {
          const toDate = parseFilterDate(
            appliedFilters.registrationDateTo,
            true,
          );
          if (regDate > toDate) return false;
        }
      }

      if (
        appliedFilters.businessType !== "" &&
        row.businessType !== appliedFilters.businessType
      )
        return false;

      if (
        appliedFilters.registrationStatus !== "" &&
        row.registrationStatus !== appliedFilters.registrationStatus
      )
        return false;

      return true;
    });
  }, [registrationsByPartner, appliedFilters]);

  const businessTypes = useMemo(() => {
    return [
      ...new Set(
        registrationsByPartner?.map((u) => u.businessType).filter(Boolean),
      ),
    ].map((type) => ({ label: type, value: type }));
  }, [registrationsByPartner]);

  const registrationSummary = useMemo(() => {
    const rows = registrationsByPartner || [];

    return {
      total: rows.length,
      pending: rows.filter((row) => row.registrationStatus === "Pending")
        .length,
      confirmed: rows.filter((row) => row.registrationStatus === "Confirmed")
        .length,
    };
  }, [registrationsByPartner]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.registrationDateFrom !== "") count++;
    if (appliedFilters.registrationDateTo !== "") count++;
    if (appliedFilters.businessType !== "") count++;
    if (appliedFilters.registrationStatus !== "") count++;
    return count;
  }, [appliedFilters]);

  useEffect(() => {
    getRegistrationsByPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="space-y-6 p-5 ">
      <section className="relative overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary p-6 shadow-sm lg:p-7">
        <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <MdPeopleOutline size={18} />
              Partner operations
            </div>
            <h2>Direct registrations</h2>
            <p className="mt-2 max-w-2xl text-sm text-textSecondary">
              Review incoming client registrations, track onboarding progress,
              and approve pending accounts from one place.
            </p>
          </div>

          <button
            type="button"
            onClick={getRegistrationsByPartner}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-borderColor bg-backgroundSecondary px-4 py-2.5 text-sm font-semibold text-textPrimary transition hover:border-accent hover:text-accent"
          >
            <MdRefresh size={18} />
            Refresh
          </button>
        </div>

        <div className="relative mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-borderColor bg-backgroundSecondary/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-textSecondary">
              Total registrations
            </p>
            <p className="mt-2 text-2xl font-bold text-textPrimary">
              {registrationSummary.total}
            </p>
          </div>
          <div className="rounded-xl border border-amber-200/60 bg-amber-50/70 p-4 dark:border-amber-900/50 dark:bg-amber-950/20">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              <MdPendingActions size={16} /> Pending review
            </p>
            <p className="mt-2 text-2xl font-bold text-amber-800 dark:text-amber-200">
              {registrationSummary.pending}
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200/60 bg-emerald-50/70 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <MdCheckCircle size={16} /> Confirmed
            </p>
            <p className="mt-2 text-2xl font-bold text-emerald-800 dark:text-emerald-200">
              {registrationSummary.confirmed}
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-borderColor bg-backgroundPrimary shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-borderColor p-4 lg:p-5">
          {/* Toggle filters */}
          <Tooltip title="Toggle filters">
            <button
              onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              className={`flex items-center gap-1 bg-accent px-3 py-1.5 rounded text-white border border-borderColor text-sm font-medium transition-colors `}
            >
              <MdFilterList size={18} />
              Filters{" "}
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-white text-accent text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </Tooltip>

          {/* Reset */}
          {activeFilterCount > 0 && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 px-3 py-1.5 rounded border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 transition-colors"
            >
              <MdClear size={16} />
              Reset all
            </button>
          )}

          {/* chips */}
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (value === "all" || value === "" || value === null)
                return null;

              return (
                <Chip
                  key={key}
                  label={`${camelCaseToCapitalCase(key)}: ${value}`}
                  onDelete={() => {
                    const updatedFilters = {
                      ...appliedFilters,
                      [key]: DEFAULT_FILTERS[key],
                    };

                    setAppliedFilters(updatedFilters);

                    setFilters((prev) => ({
                      ...prev,
                      [key]: DEFAULT_FILTERS[key],
                    }));
                  }}
                  variant="outlined"
                  sx={{
                    borderColor: "var(--border-color)",
                    color: "var(--text-secondary)",

                    "& .MuiChip-deleteIcon": {
                      color: "var(--text-primary)",
                    },

                    "& .MuiChip-deleteIcon:hover": {
                      color: "var(--text-primary)",
                      opacity: 0.8,
                    },
                  }}
                />
              );
            })}
          </div>

          {/* Result count */}
          <span className="ml-auto text-sm text-textSecondary">
            Showing <strong>{filteredData?.length}</strong> of{" "}
            <strong>{registrationsByPartner?.length ?? 0}</strong> registrations
          </span>
        </div>

        {/* ── Filter popup ── */}
        <Dialog
          open={showFilters}
          onClose={() => setFilterAnchorEl(null)}
          PaperProps={{
            sx: {
              background: "var(--background-primary)",
              borderRadius: 2,
              maxWidth: "500px",
              width: "100%",
            },
          }}
        >
          <div className="p-6 bg-backgroundPrimary text-textPrimary rounded-lg ">
            <h3 className="text-center mb-4">Filters</h3>
            <div className="grid grid-cols-1 gap-4">
              {/* Registration Date range */}
              <div className="flex flex-col gap-1">
                <span className="text-xs text-textSecondary mb-2 font-medium">
                  Registration Date
                </span>
                <div className="flex gap-2 items-center">
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={filters.registrationDateFrom}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        registrationDateFrom: e.target.value,
                      }))
                    }
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: "var(--text-primary)",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "var(--text-primary)",
                        "& fieldset": {
                          borderColor: "var(--border-color)",
                        },
                        "&:hover fieldset": {
                          borderColor: "var(--border-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--text-primary)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--text-primary)",
                        },
                        "& input": {
                          color: "var(--text-primary)",
                        },
                        "& input:focus": {
                          color: "var(--text-primary)",
                        },
                        "& input::-webkit-calendar-picker-indicator": {
                          filter: theme === "dark" ? "invert(1)" : "invert(0)",
                          cursor: "pointer",
                        },
                      },
                    }}
                  />
                  <span className="text-gray-400">–</span>
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={filters.registrationDateTo}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        registrationDateTo: e.target.value,
                      }))
                    }
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        color: "var(--text-primary)",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--text-primary)",
                      },
                      "& .MuiOutlinedInput-root": {
                        color: "var(--text-primary)",
                        "& fieldset": {
                          borderColor: "var(--border-color)",
                        },
                        "&:hover fieldset": {
                          borderColor: "var(--border-color)",
                        },
                        "&.Mui-focused": {
                          color: "var(--text-primary)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--text-primary)",
                        },
                        "& input": {
                          color: "var(--text-primary)",
                        },
                        "& input:focus": {
                          color: "var(--text-primary)",
                        },
                        "& input::-webkit-calendar-picker-indicator": {
                          filter: theme === "dark" ? "invert(1)" : "invert(0)",
                          cursor: "pointer",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-between gap-3">
                {/* businessType */}
                <FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
                  <InputLabel
                    sx={{
                      color: "var(--text-primary)",
                      "&.Mui-focused": { color: "var(--text-primary)" },
                    }}
                  >
                    Business Type
                  </InputLabel>
                  <Select
                    label="Business Type"
                    value={filters.businessType}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        businessType: e.target.value,
                      }))
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "var(--background-primary)",
                          color: "var(--text-primary)",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--text-primary)",
                      },
                      "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                      "& .MuiSelect-select": {
                        color: "var(--text-primary)",
                      },
                    }}
                  >
                    {[{ label: "All", value: "" }, ...businessTypes].map(
                      (opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{
                            background: "var(--background-primary)",
                            "&:hover": {
                              background: "var(--background-secondary)",
                            },
                            "&.Mui-selected": {
                              background: "var(--background-secondary)",
                            },
                            "&.Mui-selected:hover": {
                              background: "var(--background-secondary)",
                            },
                          }}
                        >
                          {opt.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>

                {/* registrationStatus */}
                <FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
                  <InputLabel
                    sx={{
                      color: "var(--text-primary)",
                      "&.Mui-focused": { color: "var(--text-primary)" },
                    }}
                  >
                    Registration Status
                  </InputLabel>
                  <Select
                    label="Registration Status"
                    value={filters.registrationStatus}
                    onChange={(e) =>
                      setFilters((f) => ({
                        ...f,
                        registrationStatus: e.target.value,
                      }))
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "var(--background-primary)",
                          color: "var(--text-primary)",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--border-color)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--text-primary)",
                      },
                      "& .MuiSvgIcon-root": { color: "var(--text-primary)" },
                      "& .MuiSelect-select": {
                        color: "var(--text-primary)",
                      },
                    }}
                  >
                    {DEFAULT_REGISTRATION_STATUS_OPTION.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{
                          background: "var(--background-primary)",
                          "&:hover": {
                            background: "var(--background-secondary)",
                          },
                          "&.Mui-selected": {
                            background: "var(--background-secondary)",
                          },
                          "&.Mui-selected:hover": {
                            background: "var(--background-secondary)",
                          },
                        }}
                      >
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>

                <Button variant="contained" onClick={handleApplyFilters}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Dialog>

        {/* <div className="max-w-[85vw]">
        <Paper>
          <DataGrid
            rows={registrationsByPartner}
            columns={registeredUsersColumns}
            pageSizeOptions={[50, 100, 250]}
            initialState={{
              pagination: { paginationModel: { pageSize: 50 } },
            }}
            onRowDoubleClick={(params) => handleOpen(params.row)}
            sx={{ border: 0 }}
            getRowClassName={() => "cursor-pointer"}
          />
        </Paper>
      </div> */}

        <div className="p-3 lg:p-5">
          <CustomTable
            rows={filteredData}
            columns={registeredUsersColumns}
            pageSize={50}
            pageSizeOptions={[50, 100, 250]}
            searchable={true}
            searchPlaceholder="Search registrations…"
            emptyMessage="No registrations match your current filters."
            exportName="registrations"
            isExport={true}
          />
        </div>
      </section>
    </main>
  );
};

export default Registrations;
